import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { serialWithRerun } from './orgs/activation-runner';
import { MembershipSessionService } from './orgs/membership-session.service';
import { OrgActivityService } from './orgs/org-activity.service';
import { OrgService } from './orgs/org.service';
import { VersionUpdateBannerComponent } from './version/version-update-banner.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, VersionUpdateBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly org = inject(OrgService);

  constructor() {
    // Keeps the session's token in step with its memberships. Started here,
    // at the root, because the route guards of the shell's children run
    // before the shell exists and wait on it (`settled()`) to bring a stale
    // token in line before they deny anything.
    inject(MembershipSessionService);
    // Checks in while someone who can record has GHOSTS open, so the org's
    // ready device runs while people are here (spec 2026-11-01).
    inject(OrgActivityService);

    // First-sign-in activation: when the user's pendingActivation flag is
    // true (set by the addOrgMember/createOrg callables), call the
    // idempotent markMemberActivated callable so all 'invited' member
    // docs flip to 'active'.
    //
    // The callables add the org to users/{uid}.orgIds in the same write as
    // the flag, so the callable finds the new membership right away. The
    // effect still tracks orgIds() because the callable defers (without
    // clearing the flag) while orgIds is empty, e.g. for a doc written
    // before that change; the syncOrgMemberClaims mirror write then
    // re-triggers this effect to retry. A signal that arrives while the
    // callable is awaiting is not dropped: serialWithRerun records it and
    // runs the callable once more after the in-flight call settles.
    const activate = serialWithRerun(
      () => this.org.markActivated(),
      () => this.org.pendingActivation(),
    );
    effect(() => {
      const pending = this.org.pendingActivation();
      // Read so the effect re-fires when the trigger populates the
      // mirror after a deferred attempt.
      void this.org.orgIds();
      if (pending) activate();
    });
  }
}
